import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ArrowLeft, CheckCircle2, PenLine, Send, Undo2 } from 'lucide-react';
import { Badge, StatusBadge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { SelectField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { sectionsApi } from '@/services/api/endpoints/academic';
import {
  examinationsApi,
  examScheduleApi,
  marksApi,
  type MarksEntryPayload,
} from '@/services/api/endpoints/examinations';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { MarkStatus, MarksRow, MarksSheet } from '@/types/examination';
import { isApiError } from '@/utils/ApiError';
import { orDash } from '@/utils/format';

type Entry = MarksEntryPayload['entries'][number];

/** What the teacher has typed for one row, before it is saved. */
interface Draft {
  status: MarkStatus;
  value: string;
  remarks: string;
}

/** A refusal from the server, kept on screen until the next attempt. */
interface Problem {
  message: string;
  details: string[];
}

function seed(row: MarksRow): Draft {
  return {
    status: row.markStatus,
    value: row.obtainedMarks === undefined ? '' : String(row.obtainedMarks),
    remarks: row.remarks ?? '',
  };
}

function sameDraft(a: Draft, b: Draft): boolean {
  return a.status === b.status && a.value.trim() === b.value.trim() && a.remarks.trim() === b.remarks.trim();
}

/**
 * Turns a draft into the entry the API expects, or null when there is nothing
 * to say about that child.
 *
 * The number is passed through as typed. Whether it is negative, over the
 * maximum or not a number at all is the server's call, and its sentence is the
 * one the teacher sees — the browser does not keep its own copy of the rules.
 */
function toEntry(row: MarksRow, draft: Draft): Entry | null {
  const remarks = draft.remarks.trim() === '' ? undefined : draft.remarks.trim();
  const value = draft.value.trim();

  if (draft.status === 'ABSENT' || draft.status === 'EXEMPT') {
    return { enrollmentId: row.enrollmentId, markStatus: draft.status, remarks };
  }
  if (value !== '') {
    return {
      enrollmentId: row.enrollmentId,
      markStatus: 'GRADED',
      obtainedMarks: Number(value),
      remarks,
    };
  }
  // Nothing typed: only worth sending when it clears something already recorded.
  if (row.markStatus !== 'PENDING') {
    return { enrollmentId: row.enrollmentId, markStatus: 'PENDING', remarks };
  }
  return null;
}

/** One summary for the whole sheet, read off the rows the server returned. */
function sheetState(sheet: MarksSheet): string {
  const states = new Set(sheet.rows.map((row) => row.workflowState));
  if (states.has('SUBMITTED')) {
    return 'SUBMITTED';
  }
  if (states.has('REJECTED')) {
    return 'REJECTED';
  }
  if (states.size === 1 && states.has('APPROVED')) {
    return 'APPROVED';
  }
  return 'DRAFT';
}

/**
 * Marks entry for one paper, one class at a time.
 *
 * Built like the attendance register: the whole sheet is saved in one request,
 * because thirty separate saves would make a half-marked sheet a routine
 * outcome. The grid is driven from the keyboard — Enter or Tab moves down the
 * mark column, A and E mark a child absent or exempt — since a teacher with a
 * pile of scripts should not need the mouse.
 *
 * Nothing here decides whether a mark is acceptable. Percentages, grades and
 * pass/fail come back from the server after each save, and a refusal is shown
 * in the server's own words.
 */
export function MarksEntryPage() {
  const { id: examinationId = '', paperId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionId = searchParams.get('section') ?? '';

  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [problem, setProblem] = useState<Problem | null>(null);
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [confirmingApprove, setConfirmingApprove] = useState(false);
  const [returning, setReturning] = useState(false);
  const [returnComment, setReturnComment] = useState('');
  const [returnError, setReturnError] = useState<string | null>(null);

  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // The entries of the last save, so a field error naming `entries[3]` can be
  // reported against the child it is actually about.
  const lastSent = useRef<Entry[]>([]);

  const examination = useQuery({
    queryKey: queryKeys.examinations.detail(examinationId),
    queryFn: () => examinationsApi.get(examinationId),
    enabled: examinationId !== '',
  });

  const gradeOfferingId = examination.data?.gradeOfferingId ?? '';

  /*
   * The classes that sit the paper. Sections come from the class list where
   * the user can read it, and from the paper's timetable otherwise — a subject
   * teacher often holds schedule access without the academic structure.
   */
  const sectionsParams = { gradeOfferingId, page: 0, size: 100 };
  const sections = useQuery({
    queryKey: queryKeys.sections.list(sectionsParams),
    queryFn: () => sectionsApi.list(sectionsParams),
    enabled: gradeOfferingId !== '' && can(PERMISSIONS.SECTION_VIEW),
  });
  const schedule = useQuery({
    queryKey: queryKeys.examinations.schedule(examinationId),
    queryFn: () => examScheduleApi.list(examinationId),
    enabled: examinationId !== '' && can(PERMISSIONS.EXAM_SCHEDULE_VIEW),
  });

  const sectionOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const section of sections.data?.data ?? []) {
      byId.set(section.id, section.name);
    }
    for (const sitting of schedule.data ?? []) {
      if (sitting.examinationSubjectId === paperId && !byId.has(sitting.sectionId)) {
        byId.set(sitting.sectionId, sitting.sectionName);
      }
    }
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sections.data, schedule.data, paperId]);

  // A class with a single section should not make the teacher pick it.
  useEffect(() => {
    const only = sectionOptions[0];
    if (sectionId === '' && sectionOptions.length === 1 && only !== undefined) {
      setSearchParams({ section: only.value }, { replace: true });
    }
  }, [sectionId, sectionOptions, setSearchParams]);

  const sheet = useQuery({
    queryKey: queryKeys.marks.sheet(paperId, sectionId),
    queryFn: () => marksApi.sheet(paperId, sectionId),
    enabled: paperId !== '' && sectionId !== '',
  });

  /* Seeded from what the server holds, so reopening a sheet shows the recorded
     marks — and re-seeded after every save, so the grid is always the server's
     version rather than the browser's guess at it. */
  useEffect(() => {
    if (sheet.data === undefined) {
      return;
    }
    const next: Record<string, Draft> = {};
    for (const row of sheet.data.rows) {
      next[row.enrollmentId] = seed(row);
    }
    setDrafts(next);
  }, [sheet.data]);

  const rows = sheet.data?.rows ?? [];
  const changed = rows.filter((row) => {
    const draft = drafts[row.enrollmentId];
    return draft !== undefined && !sameDraft(draft, seed(row));
  });
  const dirty = changed.length > 0;

  // Leaving with typed marks unsaved is the one loss this screen can cause.
  useEffect(() => {
    if (!dirty) {
      return;
    }
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const buildEntries = (): Entry[] =>
    rows
      .map((row) => toEntry(row, drafts[row.enrollmentId] ?? seed(row)))
      .filter((entry): entry is Entry => entry !== null);

  const describe = (error: unknown): Problem => {
    if (!isApiError(error)) {
      return { message: 'Something went wrong. Please try again.', details: [] };
    }
    const names = new Map(rows.map((row) => [row.enrollmentId, row.studentName]));
    const details = error.fieldErrors.map((fieldError) => {
      const match = /^entries\[(\d+)\]/.exec(fieldError.field);
      const entry = match?.[1] === undefined ? undefined : lastSent.current[Number(match[1])];
      const who = entry === undefined ? undefined : names.get(entry.enrollmentId);
      return who === undefined ? fieldError.message : `${who}: ${fieldError.message}`;
    });
    return { message: error.message, details };
  };

  const storeSheet = (updated: MarksSheet) => {
    queryClient.setQueryData(queryKeys.marks.sheet(paperId, sectionId), updated);
    // Paper progress on the examination page counts these rows.
    void queryClient.invalidateQueries({ queryKey: queryKeys.examinations.detail(examinationId) });
  };

  const save = useMutation({
    mutationFn: (entries: Entry[]) => {
      lastSent.current = entries;
      return marksApi.save(paperId, { sectionId, entries });
    },
    onSuccess: (updated) => {
      setProblem(null);
      storeSheet(updated);
      toast.success(`Marks saved — ${updated.markedCount} of ${updated.totalCount} marked`);
    },
    onError: (error) => setProblem(describe(error)),
  });

  const submit = useMutation({
    mutationFn: async () => {
      // Unsaved changes go in first, so what is submitted is what is on screen.
      if (dirty) {
        const entries = buildEntries();
        lastSent.current = entries;
        await marksApi.save(paperId, { sectionId, entries });
      }
      return marksApi.submit(paperId, sectionId);
    },
    onSuccess: (updated) => {
      setProblem(null);
      setConfirmingSubmit(false);
      storeSheet(updated);
      toast.success('Marks submitted for review');
    },
    onError: (error) => {
      setConfirmingSubmit(false);
      setProblem(describe(error));
      // A save that succeeded before the submit failed must still show.
      void queryClient.invalidateQueries({ queryKey: queryKeys.marks.sheet(paperId, sectionId) });
    },
  });

  const review = useMutation({
    mutationFn: (verdict: { approved: boolean; comment?: string }) =>
      marksApi.review(paperId, { sectionId, ...verdict }),
    onSuccess: (updated, verdict) => {
      setConfirmingApprove(false);
      setReturning(false);
      setReturnComment('');
      setReturnError(null);
      storeSheet(updated);
      toast.success(verdict.approved ? 'Marks approved' : 'Marks returned to the teacher');
    },
    onError: (error, verdict) => {
      if (!verdict.approved) {
        // Kept inside the dialog: the reviewer is mid-sentence.
        setReturnError(isApiError(error) ? error.message : 'Something went wrong. Please try again.');
        return;
      }
      setConfirmingApprove(false);
      handleApiError(error);
    },
  });

  // ------------------------------------------------------------- editing

  const update = (enrollmentId: string, change: Partial<Draft>) => {
    setDrafts((current) => {
      const base = current[enrollmentId] ?? { status: 'PENDING' as MarkStatus, value: '', remarks: '' };
      return { ...current, [enrollmentId]: { ...base, ...change } };
    });
  };

  /** Absent and exempt carry no mark, so choosing one empties the box; choosing it again undoes it. */
  const toggle = (enrollmentId: string, status: 'ABSENT' | 'EXEMPT') => {
    const current = drafts[enrollmentId];
    if (current?.status === status) {
      update(enrollmentId, { status: 'PENDING' });
    } else {
      update(enrollmentId, { status, value: '' });
    }
  };

  const focusRow = (index: number) => {
    const input = inputs.current[index];
    if (input !== null && input !== undefined) {
      input.focus();
      input.select();
    }
  };

  const onMarkKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number, row: MarksRow) => {
    const down = event.key === 'Enter' || event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey);
    const up = event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey);

    if (down && index + 1 < rows.length) {
      event.preventDefault();
      focusRow(index + 1);
    } else if (up && index > 0) {
      event.preventDefault();
      focusRow(index - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
    } else if (event.key.toLowerCase() === 'a' && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      toggle(row.enrollmentId, 'ABSENT');
    } else if (event.key.toLowerCase() === 'e' && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      toggle(row.enrollmentId, 'EXEMPT');
    }
  };

  // ------------------------------------------------------------- rendering

  if (examination.isError) {
    return <ErrorState error={examination.error} onRetry={() => void examination.refetch()} />;
  }
  if (examination.isPending) {
    return <LoadingState label="Loading the examination" />;
  }

  const exam = examination.data;
  const paper = exam.subjects?.find((subject) => subject.id === paperId);

  const data = sheet.data;
  const canEnter = data !== undefined && can(PERMISSIONS.MARKS_ENTER) && exam.acceptsMarks && data.editable;
  const canSubmit = data !== undefined && can(PERMISSIONS.MARKS_SUBMIT) && exam.acceptsMarks && data.editable;
  const awaitingReview = rows.some((row) => row.workflowState === 'SUBMITTED');
  const canReview = can(PERMISSIONS.MARKS_REVIEW) && awaitingReview;
  const returnedWith = rows.find((row) => row.workflowState === 'REJECTED' && row.reviewComment !== undefined)
    ?.reviewComment;
  const busy = save.isPending || submit.isPending || review.isPending;

  return (
    <div>
      <Link
        to={ROUTES.examinationDetail(exam.id)}
        className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        {exam.name}
      </Link>

      <PageHeader
        title={paper === undefined ? 'Marks' : `${paper.subjectName} marks`}
        description={
          paper === undefined
            ? undefined
            : `Out of ${paper.maxMarks}, pass mark ${paper.passingMarks}. Percentages and grades are worked out by the server when you save.`
        }
        icon={<PenLine className="h-5 w-5" aria-hidden="true" />}
        action={data !== undefined ? <StatusBadge status={sheetState(data)} /> : undefined}
      />

      <Card className="mb-4 px-4 py-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            label="Class"
            value={sectionId}
            onChange={(event) => {
              if (dirty && !window.confirm('Unsaved marks on this sheet will be lost. Switch class?')) {
                return;
              }
              setProblem(null);
              setSearchParams(event.target.value === '' ? {} : { section: event.target.value });
            }}
          >
            <option value="">Select a class…</option>
            {sectionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectField>
        </div>
        {!exam.acceptsMarks && (
          <p className="mt-3 text-xs text-muted">
            This examination is {exam.status.replaceAll('_', ' ').toLowerCase()}, so marks are read-only
            here. Entry is open while it is ongoing or completed.
          </p>
        )}
      </Card>

      {sectionId === '' ? (
        <Card>
          <EmptyState
            title="Choose a class"
            description={
              sections.isPending && schedule.isPending
                ? 'Loading the classes that sit this paper…'
                : sectionOptions.length === 0
                  ? 'No classes were found for this paper. It may not be timetabled yet.'
                  : 'Pick the class whose scripts you are marking.'
            }
          />
        </Card>
      ) : sheet.isPending ? (
        <Card>
          <LoadingState label="Loading the marks sheet" />
        </Card>
      ) : sheet.isError ? (
        <Card>
          <ErrorState error={sheet.error} onRetry={() => void sheet.refetch()} />
        </Card>
      ) : data === undefined ? null : (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
            <p className="text-sm text-muted">
              <span className="font-medium text-ink tabular-nums">{data.markedCount}</span> of{' '}
              <span className="tabular-nums">{data.totalCount}</span> marked
              {dirty && <span className="ml-2 text-warning">{changed.length} unsaved</span>}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              {canReview && (
                <>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => setReturning(true)}>
                    <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Return
                  </Button>
                  <Button size="sm" disabled={busy} onClick={() => setConfirmingApprove(true)}>
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                    Approve
                  </Button>
                </>
              )}
              {canEnter && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={save.isPending}
                  disabled={!dirty || busy}
                  onClick={() => save.mutate(buildEntries())}
                >
                  Save draft
                </Button>
              )}
              {canSubmit && (
                <Button size="sm" disabled={busy} onClick={() => setConfirmingSubmit(true)}>
                  <Send className="h-3.5 w-3.5" aria-hidden="true" />
                  Submit for review
                </Button>
              )}
            </div>
          </div>

          {returnedWith !== undefined && (
            <p className="mx-4 mt-3 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning">
              Returned by the reviewer: “{returnedWith}” Correct the marks, save, and submit again.
            </p>
          )}
          {!data.editable && !awaitingReview && sheetState(data) === 'APPROVED' && (
            <p className="mx-4 mt-3 rounded-lg bg-success-soft px-3 py-2 text-sm text-success">
              Approved. These marks will be used when results are calculated.
            </p>
          )}
          {awaitingReview && !canReview && (
            <p className="mx-4 mt-3 rounded-lg bg-info-soft px-3 py-2 text-sm text-info">
              Submitted and waiting for review. The sheet is locked until a reviewer approves or returns it.
            </p>
          )}

          {problem !== null && (
            <div role="alert" className="mx-4 mt-3 rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
              <p className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{problem.message}</span>
              </p>
              {problem.details.length > 0 && (
                <ul className="mt-1 list-disc pl-10 text-xs">
                  {problem.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {canEnter && rows.length > 0 && (
            <p className="px-4 pt-3 text-xs text-subtle">
              Enter or Tab moves down, Shift+Tab or ↑ moves up. In a mark box, press A for absent or
              E for exempt (again to undo). Absent and exempt carry no mark — a zero is a score.
            </p>
          )}

          {rows.length === 0 ? (
            <EmptyState
              title="No students on this sheet"
              description="Nobody was placed in this class when the examination began."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[760px] w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-surface-2">
                    {['Roll', 'Student', 'Mark', 'Absent / exempt', 'Remarks', '%', 'Grade', 'State'].map(
                      (header) => (
                        <th
                          key={header}
                          scope="col"
                          className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle"
                        >
                          {header}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((row, index) => {
                    const draft = drafts[row.enrollmentId] ?? seed(row);
                    const unsaved = !sameDraft(draft, seed(row));
                    const noMark = draft.status === 'ABSENT' || draft.status === 'EXEMPT';
                    return (
                      <tr key={row.enrollmentId} className={clsx(unsaved && 'bg-warning-soft/40')}>
                        <td className="px-3 py-2 font-mono text-xs text-subtle">{orDash(row.rollNumber)}</td>
                        <td className="px-3 py-2">
                          <span className="block text-ink">{row.studentName}</span>
                          <span className="block font-mono text-[11px] text-subtle">{row.studentNumber}</span>
                        </td>
                        <td className="px-3 py-2">
                          {canEnter ? (
                            <input
                              ref={(element) => {
                                inputs.current[index] = element;
                              }}
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              aria-label={`Mark for ${row.studentName}`}
                              value={draft.value}
                              readOnly={noMark}
                              placeholder={noMark ? draft.status.toLowerCase() : '—'}
                              onFocus={(event) => event.currentTarget.select()}
                              onChange={(event) =>
                                update(row.enrollmentId, { value: event.target.value, status: 'GRADED' })
                              }
                              onKeyDown={(event) => onMarkKeyDown(event, index, row)}
                              className="w-20 rounded-md border border-line bg-surface px-2 py-1.5 text-right tabular-nums text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)] read-only:bg-surface-3 read-only:text-subtle"
                            />
                          ) : (
                            <span className="tabular-nums text-ink">
                              {row.obtainedMarks ?? (row.markStatus === 'PENDING' ? '—' : '')}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {canEnter ? (
                            <div className="flex gap-1" role="group" aria-label={`Absent or exempt for ${row.studentName}`}>
                              {(['ABSENT', 'EXEMPT'] as const).map((status) => (
                                <button
                                  key={status}
                                  type="button"
                                  tabIndex={-1}
                                  aria-pressed={draft.status === status}
                                  onClick={() => toggle(row.enrollmentId, status)}
                                  className={clsx(
                                    'rounded-md border px-2 py-1 text-xs transition-colors',
                                    draft.status === status
                                      ? status === 'ABSENT'
                                        ? 'border-warning/30 bg-warning/10 text-warning'
                                        : 'border-line-strong bg-surface-3 text-ink'
                                      : 'border-line text-subtle hover:border-line-strong hover:text-muted',
                                  )}
                                >
                                  {status === 'ABSENT' ? 'Absent' : 'Exempt'}
                                </button>
                              ))}
                            </div>
                          ) : row.markStatus === 'ABSENT' || row.markStatus === 'EXEMPT' ? (
                            <StatusBadge status={row.markStatus} />
                          ) : null}
                        </td>
                        <td className="px-3 py-2">
                          {canEnter ? (
                            <input
                              type="text"
                              aria-label={`Remarks for ${row.studentName}`}
                              value={draft.remarks}
                              onChange={(event) => update(row.enrollmentId, { remarks: event.target.value })}
                              className="w-40 rounded-md border border-line bg-surface px-2 py-1.5 text-xs text-ink focus:border-accent focus:outline-none"
                            />
                          ) : (
                            <span className="text-xs text-muted">{row.remarks ?? ''}</span>
                          )}
                        </td>
                        {/* Server-computed; stale until the next save for rows being edited. */}
                        <td className="px-3 py-2 tabular-nums text-muted">
                          {unsaved ? '…' : row.percentage === undefined ? '—' : `${row.percentage}%`}
                        </td>
                        <td className="px-3 py-2">
                          {unsaved ? (
                            <span className="text-subtle">…</span>
                          ) : (
                            <span className="flex items-center gap-1.5">
                              <span className="font-medium text-ink">{orDash(row.grade)}</span>
                              {row.passing === false && <Badge tone="danger">below pass</Badge>}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge status={row.workflowState} />
                          {row.reviewComment !== undefined && row.workflowState === 'REJECTED' && (
                            <span className="mt-0.5 block max-w-[14rem] text-[11px] text-warning">
                              {row.reviewComment}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <ConfirmDialog
        open={confirmingSubmit}
        title="Submit these marks for review?"
        message={
          (dirty ? 'Your unsaved changes will be saved first. ' : '') +
          'Once submitted the sheet is locked until a reviewer approves it or returns it to you. ' +
          'The server refuses a sheet that still has unmarked students.'
        }
        confirmLabel="Submit"
        loading={submit.isPending}
        onConfirm={() => submit.mutate()}
        onCancel={() => setConfirmingSubmit(false)}
      />

      <ConfirmDialog
        open={confirmingApprove}
        title="Approve these marks?"
        message="Approved marks are the ones results are calculated from. The teacher can no longer change them."
        confirmLabel="Approve"
        loading={review.isPending}
        onConfirm={() => review.mutate({ approved: true })}
        onCancel={() => setConfirmingApprove(false)}
      />

      <Modal
        open={returning}
        title="Return these marks to the teacher?"
        description="The sheet unlocks for correction. Say what needs fixing — the teacher sees this comment."
        onClose={() => {
          setReturning(false);
          setReturnError(null);
        }}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={review.isPending}
              onClick={() => {
                setReturning(false);
                setReturnError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={review.isPending}
              onClick={() => {
                setReturnError(null);
                review.mutate({ approved: false, comment: returnComment.trim() === '' ? undefined : returnComment.trim() });
              }}
            >
              Return
            </Button>
          </>
        }
      >
        <label htmlFor="return-comment" className="mb-1.5 block text-xs font-medium text-muted">
          Reason<span className="ml-0.5 text-danger">*</span>
        </label>
        <textarea
          id="return-comment"
          rows={4}
          maxLength={500}
          value={returnComment}
          onChange={(event) => setReturnComment(event.target.value)}
          aria-invalid={returnError !== null}
          aria-describedby={returnError !== null ? 'return-comment-error' : undefined}
          className="block w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-[var(--sms-accent-ring)]"
        />
        {returnError !== null && (
          <p id="return-comment-error" className="mt-1.5 text-xs text-danger">
            {returnError}
          </p>
        )}
      </Modal>
    </div>
  );
}
